import { getBackendBase } from "../trimble/coreFiles";

export type NiceConnectAspectRow = {
  [key: string]: unknown;
};

function parseAspectResponseText(text: string): any {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

export async function queryNiceConnectAspectsForElement(args: {
  accessToken: string;
  elementId: string;
  iTwinId: string;
  iModelId: string;
  backendBase?: string | null;
}): Promise<NiceConnectAspectRow[]> {
  const backendBase = String(args.backendBase ?? getBackendBase() ?? "").trim();
  if (!backendBase) {
    throw new Error("NiceConnect backend base is not configured.");
  }

  const response = await fetch(`${backendBase.replace(/\/$/, "")}/itwin/niceconnect-aspect`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: args.accessToken,
    },
    body: JSON.stringify({
      elementId: args.elementId,
      iTwinId: args.iTwinId,
      iModelId: args.iModelId,
    }),
  });

  const text = await response.text();
  const json = parseAspectResponseText(text);

  if (!response.ok) {
    throw new Error(json?.error ?? text ?? `NiceConnect aspect request failed (${response.status}).`);
  }

  return Array.isArray(json?.aspects) ? (json.aspects as NiceConnectAspectRow[]) : [];
}

export function normalizeAspectPropertyToken(raw: unknown): string {
  return String(raw ?? "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .trim()
    .toLowerCase();
}

export function resolveAspectPropertyName(
  aspectRow: NiceConnectAspectRow | null | undefined,
  requestedNames: string[]
): string | null {
  if (!aspectRow || typeof aspectRow !== "object") return null;

  for (const requestedName of requestedNames) {
    if (Object.prototype.hasOwnProperty.call(aspectRow, requestedName)) {
      return requestedName;
    }
  }

  const requestedTokens = requestedNames
    .map((name) => normalizeAspectPropertyToken(name))
    .filter((name) => name.length > 0);
  if (requestedTokens.length === 0) return null;

  const entries = Object.keys(aspectRow);
  for (const requestedToken of requestedTokens) {
    const matches = entries.filter(
      (entry) => normalizeAspectPropertyToken(entry) === requestedToken
    );
    if (matches.length === 1) {
      return matches[0];
    }
  }

  return null;
}
